import { PartnerNotification } from '../types';
import { audioService } from './audio';

export interface PartnerWsCallbacks {
  onNotification?: (notification: PartnerNotification) => void;
  onConnected?: (user: any) => void;
  onError?: (err: string) => void;
  onClose?: () => void;
}

export class PartnerWsClient {
  private ws: WebSocket | null = null;
  private isExplicitlyClosed = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private callbacks: PartnerWsCallbacks = {};
  private activeVenueId?: string;
  private activeToken = '';

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private detachAndCloseSocket(socket: WebSocket | null): void {
    if (!socket) return;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      try {
        socket.close();
      } catch {}
    }
  }

  public connect(token: string, venueId?: string, callbacks: PartnerWsCallbacks = {}): void {
    this.clearReconnectTimer();
    if (this.ws) {
      this.detachAndCloseSocket(this.ws);
      this.ws = null;
    }

    this.activeToken = token;
    this.activeVenueId = venueId;
    this.callbacks = callbacks;
    this.isExplicitlyClosed = false;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/api/partner/ws?token=${encodeURIComponent(token)}`;

    try {
      const socket = new WebSocket(wsUrl);
      this.ws = socket;

      socket.onopen = () => {
        if (this.ws !== socket) return;
        if (this.activeVenueId && socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({
            type: 'subscribe_venue',
            venueId: this.activeVenueId
          }));
        }
      };

      socket.onmessage = (event) => {
        if (this.ws !== socket) return;
        try {
          const msg = JSON.parse(event.data);

          if (msg.type === 'connected') {
            if (this.callbacks.onConnected) {
              this.callbacks.onConnected(msg.user);
            }
          } else if (msg.type === 'notification' && msg.notification) {
            // Play notification chime
            audioService.playNotificationChime();
            if (this.callbacks.onNotification) {
              this.callbacks.onNotification(msg.notification);
            }
          }
        } catch (e) {
          console.error('[PartnerWS] Failed to parse message:', e);
        }
      };

      socket.onerror = () => {
        if (this.ws !== socket) return;
        if (this.callbacks.onError) {
          this.callbacks.onError('Partner WebSocket error');
        }
      };

      socket.onclose = () => {
        if (this.ws !== socket) return;
        this.ws = null;
        if (this.callbacks.onClose) {
          this.callbacks.onClose();
        }

        if (!this.isExplicitlyClosed) {
          this.clearReconnectTimer();
          this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            if (!this.isExplicitlyClosed && this.activeToken) {
              this.connect(this.activeToken, this.activeVenueId, this.callbacks);
            }
          }, 3000);
        }
      };
    } catch (e: any) {
      if (this.callbacks.onError) {
        this.callbacks.onError(e?.message || 'Failed to initialize partner WebSocket');
      }
    }
  }

  public subscribeVenue(venueId: string): void {
    this.activeVenueId = venueId;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'subscribe_venue',
        venueId
      }));
    }
  }

  public close(): void {
    this.isExplicitlyClosed = true;
    this.clearReconnectTimer();
    if (this.ws) {
      const socket = this.ws;
      this.ws = null;
      this.detachAndCloseSocket(socket);
    }
  }
}

export const partnerWsClient = new PartnerWsClient();
